import { View, Text } from '@react-pdf/renderer';
import { onboardingPdfReportStyle } from '@/features/pdf-report/lib/pdf-theme';

function AppxFraction({ numerator, denominator }: { numerator: string; denominator: string }) {
    return (
        <View style={onboardingPdfReportStyle.appendixFraction}>
            <Text style={onboardingPdfReportStyle.appendixFractionNumerator}>{numerator}</Text>
            <View style={onboardingPdfReportStyle.appendixFractionBar} />
            <Text style={onboardingPdfReportStyle.appendixFractionDenominator}>{denominator}</Text>
        </View>
    );
}

export function RoiAppendix({ roiDisplayType }: { roiDisplayType?: PdfRoiDisplayType }) {
    if (!roiDisplayType || roiDisplayType === 'hidden') return null;

    const title = roiDisplayType === 'totalROI'
        ? 'APPENDIX: SIMPLE ROI (RETURN ON INVESTMENT)'
        : roiDisplayType === 'CAGR'
            ? 'APPENDIX: CAGR (COMPOUND ANNUAL GROWTH RATE)'
            : 'APPENDIX: IRR (INTERNAL RATE OF RETURN)';

    return (
        <View style={onboardingPdfReportStyle.appendixBox} wrap={false}>
            <View style={onboardingPdfReportStyle.appendixHeader}><Text style={onboardingPdfReportStyle.appendixHeaderTitle}>{title}</Text></View>
            <View style={onboardingPdfReportStyle.appendixBody}>
                {roiDisplayType === 'totalROI' && (
                    <>
                        <Text style={onboardingPdfReportStyle.appendixText}>Simple ROI compares the total projected payout to the total investment, without considering the time over which payments are made.</Text>
                        <View style={onboardingPdfReportStyle.appendixEquationRow}>
                            <Text style={onboardingPdfReportStyle.appendixEquationLabel}>ROI =</Text>
                            <AppxFraction numerator="Total Payout - Total Investment" denominator="Total Investment" />
                        </View>
                    </>
                )}
                {roiDisplayType === 'CAGR' && (
                    <>
                        <Text style={onboardingPdfReportStyle.appendixText}>CAGR provides a "smoothed out" annualized rate of return, showing what the annual growth rate would have been if it had occurred evenly over the entire period. This is useful for comparing a policy's performance to other types of long-term investments.</Text>
                        <View style={onboardingPdfReportStyle.appendixEquationRow}>
                            <Text style={onboardingPdfReportStyle.appendixEquationLabel}>CAGR =</Text>
                            <Text style={onboardingPdfReportStyle.appendixEquationTail}>(</Text>
                            <AppxFraction numerator="Total Payout" denominator="Present Value of Investment" />
                            <Text style={onboardingPdfReportStyle.appendixEquationTail}>)^(1/Years) - 1</Text>
                        </View>
                        <Text style={onboardingPdfReportStyle.appendixText}>The CAGR calculation includes a discount rate used to calculate the Present Value of Investment. This rate is only relevant for policies with future premium payments. A higher discount rate means future premiums are considered less costly in today's dollars. For single-premium (one-time) investments, the discount rate has no impact.</Text>
                    </>
                )}
                {roiDisplayType === 'IRR' && (
                    <>
                        <Text style={onboardingPdfReportStyle.appendixText}>IRR represents the annualized effective rate of return that makes the net present value of all cash flows equal to zero. Unlike CAGR, IRR accounts for the exact timing of all premium payments and benefit payouts throughout the policy lifetime, providing a more precise measure of investment performance when cash flows occur at irregular intervals.</Text>
                        <View style={onboardingPdfReportStyle.appendixEquationRow}>
                            <Text style={onboardingPdfReportStyle.appendixEquationLabel}>Sum(i=1..n)</Text>
                            <Text style={onboardingPdfReportStyle.appendixEquationTail}> CFi / (1 + IRR)^(days_i/365) = 0</Text>
                        </View>
                        <Text style={onboardingPdfReportStyle.appendixText}>Where CFi is the cash flow at time i (negative for investments, positive for returns), and days_i is the number of days from the base year to cash flow i. IRR is calculated using the XIRR method, which uses the actual dates of cash flows rather than assuming annual periods.</Text>
                    </>
                )}
            </View>
        </View>
    );
}
